import { useState } from 'react'
import { Formik, Form } from 'formik'
import { authAPI } from '../../../services/api'
import { Button, FormikPasswordInput, Alert, Card } from '../../ui'
import { changePasswordSchema } from '../../../utils/validationSchemas'
import { useDispatch } from 'react-redux'
import { logout } from '../../../store/slices/authSlice'
import { useNavigate } from 'react-router-dom'

const ChangePassword = () => {
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsSubmitting(true)
      setError(null)
      setSuccess(null)

      const data = {
        current_password: values.current_password,
        password: values.password,
        password_confirmation: values.password_confirmation,
      }

      const response = await authAPI.changePassword(data)

      if (response.data.success) {
        setSuccess(response.data.message || 'Password changed successfully. Please login again.')
        resetForm()
        
        // Logout user and redirect to login after 2 seconds
        setTimeout(() => {
          dispatch(logout())
          navigate('/login')
        }, 2000)
      } else {
        setError(response.data.message || 'Failed to change password')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to change password. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h2>
        <p className="text-sm text-gray-600 mb-6">
          Update your password regularly to keep your account secure. After changing your password, you will be logged out and need to login again.
        </p>

        {error && (
          <div className="mb-4">
            <Alert variant="danger" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {success && (
          <div className="mb-4">
            <Alert variant="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          </div>
        )}

        <Formik
          initialValues={{
            current_password: '',
            password: '',
            password_confirmation: '',
          }}
          validationSchema={changePasswordSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting: formikSubmitting }) => (
            <Form className="space-y-4" noValidate>
              <FormikPasswordInput
                id="current_password"
                name="current_password"
                label="Current Password"
                placeholder="Enter your current password"
                disabled={isSubmitting || formikSubmitting}
                required
              />

              <FormikPasswordInput
                id="password"
                name="password"
                label="New Password"
                placeholder="Enter your new password"
                disabled={isSubmitting || formikSubmitting}
                required
              />

              <FormikPasswordInput
                id="password_confirmation"
                name="password_confirmation"
                label="Confirm New Password"
                placeholder="Confirm your new password"
                disabled={isSubmitting || formikSubmitting}
                required
              />

              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting || formikSubmitting}
                  disabled={isSubmitting || formikSubmitting}
                >
                  Change Password
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Card>
  )
}

export default ChangePassword

